import React,{useState,useEffect} from 'react'
import {motion,AnimatePresence} from 'framer-motion'
import {useFamily} from '../../contexts/FamilyContext'
import {useAuth} from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import TaskCard from '../tasks/TaskCard'
import CreateTaskModal from '../tasks/CreateTaskModal'

const {FiChevronLeft,FiChevronRight,FiPlus,FiCalendar,FiClock,FiUsers}=FiIcons

const CalendarView=()=> {
  const {tasks,completions,loading}=useFamily()
  const {family}=useAuth()
  const [currentDate,setCurrentDate]=useState(new Date())
  const [viewMode,setViewMode]=useState('month') // 'day', 'week', 'month'
  const [selectedDate,setSelectedDate]=useState(new Date())
  const [showCreateTask,setShowCreateTask]=useState(false)
  const [selectedTasks,setSelectedTasks]=useState([])

  // Get tasks for a specific date
  const getTasksForDate=(date)=> {
    const dateStr=date.toISOString().split('T')[0]
    return tasks.filter(task=> {
      // Check if task is active for this date
      const startDate=task.start_date ? new Date(task.start_date) : null
      const endDate=task.end_date ? new Date(task.end_date) : null
      const dueDate=task.due_date ? new Date(task.due_date) : null

      // Task should be active on this date
      if (startDate && date < startDate) return false
      if (endDate && date > endDate) return false

      // Check recurring pattern
      switch (task.recurring_pattern) {
        case 'once':
          return dueDate ? date.toDateString()===dueDate.toDateString() : 
                 startDate ? date.toDateString()===startDate.toDateString() : false
        case 'daily':
          return startDate ? date >=startDate : true
        case 'weekly':
          if (!task.recurring_days || task.recurring_days.length===0) return false
          const dayOfWeek=date.getDay()
          return task.recurring_days.includes(dayOfWeek)
        case 'monthly':
          if (!startDate) return false
          return date.getDate()===startDate.getDate() && date >=startDate
        default:
          return false
      }
    })
  }

  // Get completions for a specific date
  const getCompletionsForDate=(date)=> {
    const dateStr=date.toDateString()
    return completions.filter(completion=> 
      new Date(completion.completed_at).toDateString()===dateStr
    )
  }

  // Navigation functions
  const navigateDate=(direction)=> {
    const newDate=new Date(currentDate)
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction==='next' ? 1 : -1))
        break
      case 'week':
        newDate.setDate(newDate.getDate() + (direction==='next' ? 7 : -7))
        break
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction==='next' ? 1 : -1))
        break
    }
    setCurrentDate(newDate)
  }

  const goToToday=()=> {
    setCurrentDate(new Date())
    setSelectedDate(new Date())
  }

  // Get child by ID
  const getChild=(childId)=> {
    return family?.children?.find(c=> c.id===childId)
  }

  // Format date for display
  const formatDate=(date)=> {
    switch (viewMode) {
      case 'day':
        return date.toLocaleDateString('en-US',{
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      case 'week':
        const startOfWeek=getStartOfWeek(date)
        const endOfWeek=new Date(startOfWeek)
        endOfWeek.setDate(endOfWeek.getDate() + 6)
        return `${startOfWeek.toLocaleDateString('en-US',{month: 'short',day: 'numeric'})} - ${endOfWeek.toLocaleDateString('en-US',{month: 'short',day: 'numeric',year: 'numeric'})}`
      case 'month':
        return date.toLocaleDateString('en-US',{month: 'long',year: 'numeric'})
      default:
        return date.toLocaleDateString()
    }
  }

  // Get start of week (Sunday)
  const getStartOfWeek=(date)=> {
    const start=new Date(date)
    start.setDate(start.getDate() - start.getDay())
    return start
  }

  // Get days in current month
  const getDaysInMonth=(date)=> {
    const year=date.getFullYear()
    const month=date.getMonth()
    const firstDay=new Date(year,month,1)
    const lastDay=new Date(year,month + 1,0)
    const startDate=getStartOfWeek(firstDay)
    const days=[]
    const current=new Date(startDate)

    // Generate 6 weeks worth of days
    for (let week=0;week < 6;week++) {
      for (let day=0;day < 7;day++) {
        days.push(new Date(current))
        current.setDate(current.getDate() + 1)
      }
    }
    return days
  }

  // Get week days
  const getWeekDays=(date)=> {
    const startOfWeek=getStartOfWeek(date)
    const days=[]
    for (let i=0;i < 7;i++) {
      const day=new Date(startOfWeek)
      day.setDate(day.getDate() + i)
      days.push(day)
    }
    return days
  }

  // Handle date click
  const handleDateClick=(date)=> {
    setSelectedDate(date)
    const dayTasks=getTasksForDate(date)
    setSelectedTasks(dayTasks)
  }

  // Render Day View
  const renderDayView=()=> {
    const dayTasks=getTasksForDate(currentDate)
    const dayCompletions=getCompletionsForDate(currentDate)

    return (
      <div className="space-y-6">
        {/* Day Header */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Tasks for {currentDate.toLocaleDateString('en-US',{weekday: 'long',month: 'long',day: 'numeric'})}
            </h3>
            <div className="flex items-center gap-2">
              <Badge variant="info">{dayTasks.length} tasks</Badge>
              <Badge variant="success">{dayCompletions.length} completed</Badge>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-3">
            {dayTasks.length > 0 ? (
              dayTasks.map(task=> {
                const child=task.assigned_to ? getChild(task.assigned_to) : null
                const isCompleted=dayCompletions.some(c=> c.task_id===task.id)

                return (
                  <motion.div
                    key={task.id}
                    initial={{opacity: 0,x: -20}}
                    animate={{opacity: 1,x: 0}}
                    className={`flex items-center gap-4 p-3 rounded-lg border-2 ${
                      isCompleted ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                    }`}
                  >
                    {child && (
                      <div className="text-2xl">{child.avatar || '👦'}</div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${
                          isCompleted ? 'text-green-800 dark:text-green-300 line-through' : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {task.title}
                        </h4>
                        <Badge variant="primary" size="sm">{task.points} pts</Badge>
                        {task.estimated_time_minutes && (
                          <Badge variant="secondary" size="sm">
                            <SafeIcon icon={FiClock} className="text-xs mr-1" />
                            {task.estimated_time_minutes}m
                          </Badge>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{task.description}</p>
                      )}
                      {child && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Assigned to {child.name}</p>
                      )}
                    </div>
                    {isCompleted && (
                      <div className="text-green-500 dark:text-green-400">
                        <SafeIcon icon={FiClock} className="text-xl" />
                      </div>
                    )}
                  </motion.div>
                )
              })
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📅</div>
                <p className="text-gray-600 dark:text-gray-400">No tasks scheduled for this day</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    )
  }

  // Render Week View
  const renderWeekView=()=> {
    const weekDays=getWeekDays(currentDate)

    return (
      <div className="grid grid-cols-7 gap-2">
        {/* Day headers */}
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((dayName,index)=> (
          <div key={dayName} className="p-2 text-center font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
            {dayName}
          </div>
        ))}

        {/* Week days */}
        {weekDays.map((day,index)=> {
          const dayTasks=getTasksForDate(day)
          const dayCompletions=getCompletionsForDate(day)
          const isToday=day.toDateString()===new Date().toDateString()
          const isSelected=day.toDateString()===selectedDate.toDateString()

          return (
            <motion.div
              key={index}
              whileHover={{scale: 1.02}}
              className={`min-h-[120px] p-2 border cursor-pointer transition-colors ${
                isSelected ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20' : 
                isToday ? 'border-secondary-500 bg-secondary-50 dark:border-secondary-400 dark:bg-secondary-900/20' : 
                'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
              onClick={()=> handleDateClick(day)}
            >
              <div className="text-center mb-2">
                <span className={`text-sm font-medium ${
                  isToday ? 'text-secondary-600 dark:text-secondary-400' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {day.getDate()}
                </span>
              </div>

              {/* Tasks for this day */}
              <div className="space-y-1">
                {dayTasks.slice(0,3).map(task=> {
                  const child=task.assigned_to ? getChild(task.assigned_to) : null
                  const isCompleted=dayCompletions.some(c=> c.task_id===task.id)

                  return (
                    <div
                      key={task.id}
                      className={`text-xs p-1 rounded flex items-center gap-1 ${
                        isCompleted ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                      }`}
                    >
                      {child && (
                        <span className="text-sm">{child.avatar}</span>
                      )}
                      <span className="truncate flex-1">{task.title}</span>
                    </div>
                  )
                })}
                {dayTasks.length > 3 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    +{dayTasks.length - 3} more
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    )
  }

  // Render Month View
  const renderMonthView=()=> {
    const monthDays=getDaysInMonth(currentDate)
    const currentMonth=currentDate.getMonth()

    return (
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((dayName)=> (
          <div key={dayName} className="p-3 text-center font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
            {dayName}
          </div>
        ))}

        {/* Month days */}
        {monthDays.map((day,index)=> {
          const dayTasks=getTasksForDate(day)
          const dayCompletions=getCompletionsForDate(day)
          const isCurrentMonth=day.getMonth()===currentMonth
          const isToday=day.toDateString()===new Date().toDateString()
          const isSelected=day.toDateString()===selectedDate.toDateString()

          return (
            <motion.div
              key={index}
              whileHover={{scale: 1.02}}
              className={`min-h-[100px] p-2 border cursor-pointer transition-colors ${
                !isCurrentMonth ? 'text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-900' : 
                isSelected ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20' : 
                isToday ? 'border-secondary-500 bg-secondary-50 dark:border-secondary-400 dark:bg-secondary-900/20' : 
                'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
              onClick={()=> handleDateClick(day)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium ${
                  !isCurrentMonth ? 'text-gray-400 dark:text-gray-600' : 
                  isToday ? 'text-secondary-600 dark:text-secondary-400' : 
                  'text-gray-700 dark:text-gray-300'
                }`}>
                  {day.getDate()}
                </span>
                {dayTasks.length > 0 && (
                  <Badge variant="info" size="sm" className="text-xs">
                    {dayTasks.length}
                  </Badge>
                )}
              </div>

              {/* Avatar indicators for assigned tasks */}
              <div className="flex flex-wrap gap-1 mb-1">
                {dayTasks.slice(0,4).map(task=> {
                  const child=task.assigned_to ? getChild(task.assigned_to) : null
                  const isCompleted=dayCompletions.some(c=> c.task_id===task.id)
                  if (!child) return null

                  return (
                    <div
                      key={task.id}
                      className={`text-sm ${isCompleted ? 'opacity-50' : ''}`}
                      title={`${task.title} - ${child.name}`}
                    >
                      {child.avatar}
                    </div>
                  )
                })}
                {dayTasks.length > 4 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    +{dayTasks.length - 4}
                  </div>
                )}
              </div>

              {/* Completion indicator */}
              {dayCompletions.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-green-600 dark:text-green-400">{dayCompletions.length}</span>
                </div>
              )}
            </motion.div>
          )
        })}
      </div>
    )
  }

  // Render selected day tasks sidebar
  const renderSelectedDayTasks=()=> {
    if (!selectedTasks.length) return null

    const dayCompletions=getCompletionsForDate(selectedDate)

    return (
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Tasks for {selectedDate.toLocaleDateString('en-US',{weekday: 'long',month: 'long',day: 'numeric'})}
        </h3>
        <div className="space-y-3">
          {selectedTasks.map(task=> {
            const child=task.assigned_to ? getChild(task.assigned_to) : null
            const isCompleted=dayCompletions.some(c=> c.task_id===task.id)

            return (
              <div
                key={task.id}
                className={`p-3 rounded-lg border ${
                  isCompleted ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 
                  'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  {child && (
                    <div className="text-xl">{child.avatar}</div>
                  )}
                  <div className="flex-1">
                    <h4 className={`font-medium ${
                      isCompleted ? 'text-green-800 dark:text-green-300 line-through' : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {task.title}
                    </h4>
                    {child && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{child.name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="primary" size="sm">{task.points} pts</Badge>
                    {isCompleted && (
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    )}
                  </div>
                </div>
                {task.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{task.description}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="capitalize">{task.category}</span>
                  {task.estimated_time_minutes && (
                    <>
                      <span>•</span>
                      <span>{task.estimated_time_minutes}m</span>
                    </>
                  )}
                  <span>•</span>
                  <span className="capitalize">{task.recurring_pattern}</span>
                </div>
              </div>
            )
          })}
        </div>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading calendar...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={()=> navigateDate('prev')}
              >
                <SafeIcon icon={FiChevronLeft} />
              </Button>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
                {formatDate(currentDate)}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={()=> navigateDate('next')}
              >
                <SafeIcon icon={FiChevronRight} />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
            >
              Today
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Selector */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {[
                {value: 'day',label: 'Day'},
                {value: 'week',label: 'Week'},
                {value: 'month',label: 'Month'}
              ].map((mode)=> (
                <button
                  key={mode.value}
                  onClick={()=> setViewMode(mode.value)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode===mode.value 
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={()=> setShowCreateTask(true)}
            >
              <SafeIcon icon={FiPlus} />
              Add Task
            </Button>
          </div>
        </div>
      </Card>

      {/* Calendar Content */}
      <div className={`grid gap-6 ${
        (viewMode==='month' || viewMode==='week') && selectedTasks.length > 0 
          ? 'lg:grid-cols-4' 
          : 'grid-cols-1'
      }`}>
        <div className={`${
          (viewMode==='month' || viewMode==='week') && selectedTasks.length > 0 
            ? 'lg:col-span-3' 
            : 'col-span-1'
        }`}>
          <Card className="p-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={viewMode}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                exit={{opacity: 0,y: -20}}
                transition={{duration: 0.3}}
              >
                {viewMode==='day' && renderDayView()}
                {viewMode==='week' && renderWeekView()}
                {viewMode==='month' && renderMonthView()}
              </motion.div>
            </AnimatePresence>
          </Card>
        </div>

        {/* Selected Day Tasks Sidebar */}
        {(viewMode==='month' || viewMode==='week') && selectedTasks.length > 0 && (
          <motion.div
            initial={{opacity: 0,x: 20}}
            animate={{opacity: 1,x: 0}}
            className="lg:col-span-1"
          >
            {renderSelectedDayTasks()}
          </motion.div>
        )}
      </div>

      {/* Legend */}
      <Card className="p-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-500 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Selected Day</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-secondary-500 rounded"></div>
            <span className="text-gray-700 dark:text-gray-300">Today</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-700 dark:text-gray-300">Completed Tasks</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">👦👧</span>
            <span className="text-gray-700 dark:text-gray-300">Child Avatars (Assigned Tasks)</span>
          </div>
        </div>
      </Card>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showCreateTask}
        onClose={()=> setShowCreateTask(false)}
      />
    </div>
  )
}

export default CalendarView